import type { Cube } from '@cubejs-client/core'
import { ArrowLeft, Printer } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { EditorContent, useEditor } from '@tiptap/react'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Button } from '@/components/ui/button'
import { RunPdfButton } from '@/features/documents/runs/RunPdfButton'
import { createDocumentExtensions } from '@/features/documents/editor/extensions'
import { DOCUMENT_CLASS } from '@/features/documents/nodes/document-class'
import { FrozenDocumentSource } from '@/features/documents/nodes/frozen-source'
import { useCubeMeta } from '@/hooks/use-cube-meta'
import { messages } from '@/lib/messages'
import { periodLabel } from '@/lib/period'
import { reportDocumentSchema, type ReportDocument } from '@/lib/report-document'
import { cn } from '@/lib/utils'
import { useBiReport } from '@/services/bi/reports'
import { useRun, type DocumentRun } from '@/services/bi/runs'

import { Letterhead, DraftWatermark } from './Letterhead'
import { buildPageStyle } from './page-style'
import { useReportReady } from './use-report-ready'
import './print.css'

const t = messages.documents.runs.print

/** Sekme başlığı tarayıcının "PDF olarak kaydet" dosya adıdır: "<başlık> - <dönem>". */
function useDocumentTitle(title: string) {
  useEffect(() => {
    const previous = document.title
    document.title = title
    return () => {
      document.title = previous
    }
  }, [title])
}

/** Baskı kâğıda açık basar: uygulamanın koyu teması bu sayfa açıkken kapatılır. */
function useForcedLightTheme() {
  useEffect(() => {
    const root = document.documentElement
    const wasDark = root.classList.contains('dark')
    root.classList.remove('dark')
    return () => {
      root.classList.toggle('dark', wasDark)
    }
  }, [])
}

function PrintDocument({
  run,
  template,
  views,
}: {
  run: DocumentRun
  template: ReportDocument
  views: ReadonlyMap<string, Cube>
}) {
  const editor = useEditor({
    extensions: createDocumentExtensions(),
    content: run.content,
    editable: false,
  })
  const pageRef = useRef<HTMLDivElement>(null)
  useReportReady(pageRef)
  useDocumentTitle(`${template.title} - ${periodLabel(run.period)}`)
  const pageStyle = buildPageStyle({
    orientation: template.orientation,
    header: `${template.title} · ${periodLabel(run.period)}`,
    pageWord: t.page,
  })

  return (
    <div className="bg-background text-foreground min-h-screen">
      <style>{pageStyle}</style>
      {run.status === 'draft' && <DraftWatermark />}

      <div className="report-doc-noprint border-border flex items-center justify-between gap-3 border-b px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          render={<Link to={`/belgeler/${run.report_id}/${run.id}`} />}
          nativeButton={false}
        >
          <ArrowLeft />
          {t.back}
        </Button>
        <div className="flex items-start gap-2">
          <RunPdfButton reportId={run.report_id} runId={run.id} />
          <Button onClick={() => window.print()}>
            <Printer />
            {t.action}
          </Button>
        </div>
      </div>

      <div
        ref={pageRef}
        className={cn(
          'report-doc-page mx-auto w-full p-8',
          template.orientation === 'landscape' ? 'max-w-[297mm]' : 'max-w-[210mm]',
        )}
      >
        <Letterhead
          title={template.title}
          period={run.period}
          createdAt={run.created_at}
          refreshedAt={run.refreshed_at}
        />
        <FrozenDocumentSource
          config={{
            views,
            queries: template.queries,
            filters: template.filters,
            filterValues: run.filters,
            period: run.period,
            data: run.data,
            printMode: true,
          }}
        >
          <EditorContent editor={editor} className={`report-doc ${DOCUMENT_CLASS}`} />
        </FrozenDocumentSource>
      </div>
    </div>
  )
}

/**
 * Dönem kopyasının baskı sayfası (`/yazdir/belge/:id/:runId`): `AppShell` dışında, açık tema.
 * Rakamlar `run.data`'dan çizilir (Cube'a sorgu yok); widget'lar `printMode`'dadır. Sayfa boyutu,
 * üst bilgi ve "Sayfa x / y" çalışma anında üretilen `@page` kuralından gelir.
 */
export function PrintRunPage() {
  const { id, runId } = useParams()
  const run = useRun(id, runId)
  const report = useBiReport(id)
  const meta = useCubeMeta()
  useForcedLightTheme()

  const failure = run.error ?? report.error ?? meta.error
  if (failure) return <ErrorState error={failure} />
  if (!run.data || !report.data || !meta.data) return <Loading rows={8} />

  const template = reportDocumentSchema.safeParse(report.data.definition)
  if (!template.success) {
    return (
      <p role="alert" className="text-destructive p-4 text-sm">
        {messages.documents.editor.invalidDefinition}
      </p>
    )
  }
  const views = new Map(
    meta.data.cubes.filter((cube) => cube.type === 'view').map((cube) => [cube.name, cube]),
  )
  return <PrintDocument key={run.data.id} run={run.data} template={template.data} views={views} />
}
